import {Detail, FlexColumn, SearchField, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {groupBy} from 'lodash';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import type {NovaAgentSummary} from './novaAgentSummary';
import {getAvailableAgents, subscribeToAvailableAgents} from './novaInvocationStore';

export interface NovaAgentPickerProps {
	taskId: string;
	// Disambiguates two mounts of the *same* task's control on one page (e.g. My Work renders a
	// starred task in both its favourited-cards section and its regular task-row section) — each
	// caller site passes its own stable string ('card', 'my-work-row', ...). Omitted, this is a
	// no-op: the caret id and toggle-event key are exactly what they were before this existed, so
	// every other mounting surface (with only one instance per task on the page) is unaffected.
	instanceKey?: string;
	onSelectAgent: (agentId: string) => void;
}

interface AgentCategoryGroup {
	category: string;
	agents: NovaAgentSummary[];
}

interface PopoverPosition {
	top: number;
	left: number;
}

function groupByCategory(agents: NovaAgentSummary[]): AgentCategoryGroup[] {
	// Object key insertion order preserves each category's first-seen position, matching the
	// order agents appear in MOCK_AGENTS.
	return Object.entries(groupBy(agents, agent => agent.category)).map(([category, categoryAgents]) => ({
		category,
		agents: categoryAgents,
	}));
}

// A single string key naming one control instance — just taskId when there's only one mount of
// that task on the page (the common case, and byte-identical to this project's original,
// pre-instanceKey format), or taskId scoped by instanceKey when a caller says there could be more
// than one. NovaInvocationControl.tsx imports this rather than building its own copy of the
// format, so the caret's id and this picker's lookup of it can never drift apart.
export function novaInstanceKey(taskId: string, instanceKey?: string): string {
	return instanceKey ? `${taskId}:${instanceKey}` : taskId;
}

// The caret that opens this picker (NovaInvocationControl) gives it a stable id to find via the
// DOM rather than a prop-drilled ref, since the two components are deliberately decoupled and
// only communicate over the event bus.
export function anchorElementId(taskId: string, instanceKey?: string): string {
	return `nova-invocation-caret-${novaInstanceKey(taskId, instanceKey)}`;
}

// Card/row layouts clip or reflow absolutely-positioned children placed inline with their own
// content — an in-flow popover here would overlap the task name/deadline text directly beneath
// the caret. Portaling to document.body and positioning from the anchor's real screen coordinates
// makes the popover float independently of whatever container mounts this control.
function computePosition(taskId: string, instanceKey?: string): PopoverPosition | null {
	const anchor = document.getElementById(anchorElementId(taskId, instanceKey));
	if (!anchor) return null;
	const rect = anchor.getBoundingClientRect();
	return {top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX};
}

// Mirrors the mockup's small-caps section labels ("DEFAULT", "SCOPING & ESTIMATES", ...) — a
// text-transform layered on the design system's own Detail component rather than a hardcoded
// font, matching the same convention already used for group/section labels elsewhere in this
// codebase (e.g. TaskSetListPane's HeaderCell, CreateTaskSetDrawer's Stat label).
const CategoryLabel = styled.div`
	margin: 12px 0 4px;
	text-transform: uppercase;
	letter-spacing: 0.4px;

	&:first-child {
		margin-top: 0;
	}
`;

// A plain <button> with inline styles (as this row was before) can't express :hover — converted
// to a styled-component for that alone. $highlighted tints the row the same way the mockup
// highlights its current default agent — see the two callers of this ($isAccountDefault and
// $isImplicitChoice below) for why there are two, distinct reasons a row can set it.
const AgentRow = styled.button<{$highlighted: boolean}>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	width: 100%;
	text-align: left;
	border: none;
	border-radius: 6px;
	padding: 8px 10px;
	cursor: pointer;
	background: ${({$highlighted}) =>
		$highlighted ? tokensForThemeProvider?.sky?.sys?.color?.background?.primary?.['1'] ?? 'transparent' : 'transparent'};

	// A highlighted row hovers to a stronger shade of the same blue family, not the plain rows'
	// neutral hover — the unconditional neutral hover this replaced was overriding the highlight
	// background entirely, so hovering the highlighted row visibly flashed it back to white/gray.
	&:hover {
		background: ${({$highlighted}) =>
			$highlighted
				? tokensForThemeProvider?.sky?.sys?.color?.background?.primary?.['2'] ?? 'rgba(20,20,80,0.08)'
				: tokensForThemeProvider?.sky?.sys?.color?.background?.neutral?.['2'] ?? 'rgba(20,20,40,0.04)'};
	}
`;

function popoverStyle(position: PopoverPosition): React.CSSProperties {
	return {
		position: 'absolute',
		top: position.top,
		left: position.left,
		zIndex: 1000,
		minWidth: 280,
		maxHeight: 320,
		overflowY: 'auto',
		// Optional chaining for the same reason as styleTokens.ts / NovaInvocationControl.tsx:
		// this module can load transitively through the shared web-components barrel, and an
		// unrelated test's partial '@forecast-it/design-system' mock shouldn't be able to crash it.
		background: tokensForThemeProvider?.sky?.sys?.color?.background?.neutral['1'],
		border: `1px solid ${tokensForThemeProvider?.sky?.ref?.color?.coolneutral['200']}`,
		borderRadius: 6,
		// No design-system floating/popover shadow token exists (only flat button/card elevation
		// shadows); this soft blur is a deliberate one-off rather than a mismatched token.
		boxShadow: '0 4px 16px rgba(20,20,40,0.16)',
		padding: 8,
	};
}

export const NovaAgentPicker = (props: NovaAgentPickerProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [position, setPosition] = useState<PopoverPosition | null>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	// The store, not this component's own state, is the source of truth for the agent list. This
	// counter's value is never read for itself — bumping it forces the re-render (and the groups
	// memo below, which depends on it) whenever the store changes underneath an already-mounted
	// picker, e.g. a slow fetch resolving after the picker opened.
	const [agentsVersion, setAgentsVersion] = useState(0);

	useEffect(() => subscribeToAvailableAgents(() => setAgentsVersion(n => n + 1)), []);

	// A portaled popover with nothing listening for an outside click never closes on its own — the
	// only close path otherwise is re-clicking the caret. Runs on mousedown, not click: the same
	// physical click that opens the picker (via the caret's own onClick, over the event bus) would
	// otherwise be seen as an "outside" click by a same-tick click listener and close it right back.
	useEffect(() => {
		if (!open) return undefined;
		const handlePointerDown = (event: MouseEvent): void => {
			const target = event.target as Node;
			// The caret's own click already toggles via the event bus above — let that be the only
			// handler for it, or this listener and the toggle would race and reopen what it just closed.
			if (document.getElementById(anchorElementId(props.taskId, props.instanceKey))?.contains(target)) return;
			if (popoverRef.current?.contains(target)) return;
			setOpen(false);
			setQuery('');
		};
		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [open, props.taskId, props.instanceKey]);

	useEffect(() => {
		if (!open) return undefined;
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open]);

	// Position is computed once at open time from the anchor's real screen coordinates and never
	// updated after — scrolling any ancestor (e.g. the scoping table's own inner scroll container)
	// moves the caret without moving this portaled popover, leaving it visually detached. Closing
	// on scroll is simpler and safer than tracking position live, and matches how a native <select>
	// dropdown behaves. The capture phase is required: a nested container's own scroll event does
	// not bubble to document, only capture-phase listeners on an ancestor see it — but that also
	// means the popover's own agent list (overflowY: auto) reports its internal scroll the same
	// way, so a scroll targeting the popover itself is excluded rather than closing on its own list.
	useEffect(() => {
		if (!open) return undefined;
		const handleScroll = (event: Event): void => {
			if (popoverRef.current?.contains(event.target as Node)) return;
			setOpen(false);
			setQuery('');
		};
		document.addEventListener('scroll', handleScroll, true);
		return () => document.removeEventListener('scroll', handleScroll, true);
	}, [open]);

	useEffect(() => {
		// Reads `open` directly (a plain event-handler closure, not a setState updater) rather than
		// deriving it via a functional setOpen update — computePosition/setPosition are side
		// effects, and calling side effects from inside a state updater isn't safe: React may
		// invoke an updater more than once for the same update.
		const handler = (toggledKey: string): void => {
			if (toggledKey !== novaInstanceKey(props.taskId, props.instanceKey)) return;
			const next = !open;
			if (next) setPosition(computePosition(props.taskId, props.instanceKey));
			setOpen(next);
			setQuery('');
		};
		subscribe<string>(EVENT_ID.NOVA_TOGGLE_PICKER, handler);
		return () => unsubscribe<string>(EVENT_ID.NOVA_TOGGLE_PICKER, handler);
	}, [props.taskId, props.instanceKey, open]);

	// There is no account-default agent yet — that's still an open product decision (isDefault is
	// hardcoded false by the backend today; see NovaAgentLogic.getAgentsForCustomer). But
	// useNovaInvocationState's onAction already has to pick *something* when Action is clicked
	// directly rather than through this picker, and it picks whichever agent is first in the
	// resolved list. Highlighting that same row here — without calling it "default" anywhere in
	// copy — just shows what a bare Action click actually does today; it is not a claim about
	// which agent the account defaults to.
	const firstAgentId = useMemo(() => getAvailableAgents()[0]?.id, [agentsVersion]);

	const groups = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		const filtered = getAvailableAgents().filter(
			agent =>
				!normalizedQuery ||
				agent.title.toLowerCase().includes(normalizedQuery) ||
				(agent.description ?? '').toLowerCase().includes(normalizedQuery)
		);
		return groupByCategory(filtered);
	}, [query, agentsVersion]);

	if (!open) return null;

	// In jsdom (unit tests), getBoundingClientRect returns an all-zero rect, so position may be
	// null there — {top: 0, left: 0} is a reasonable fallback for that environment and is never
	// visible in a real browser (a real anchor always yields a real rect).
	const style = popoverStyle(position ?? {top: 0, left: 0});

	const content = (
		<div
			ref={popoverRef}
			role="listbox"
			aria-label={formatMessage({id: 'nova_invocation.picker.aria_label'})}
			data-cy="nova-agent-picker"
			style={style}
		>
			<Detail data-cy="nova-agent-picker-header">{formatMessage({id: 'nova_invocation.picker.header'})}</Detail>
			<div style={{margin: '6px 0 8px'}}>
				<SearchField
					isExpandable={false}
					value={query}
					onChange={setQuery}
					placeholder={formatMessage({id: 'nova_invocation.picker.search_placeholder'})}
					data-cy="nova-agent-picker-search"
				/>
			</div>
			{groups.length === 0 && (
				<Text data-cy="nova-agent-picker-empty">{formatMessage({id: 'nova_invocation.picker.empty_state'})}</Text>
			)}
			{groups.map(group => (
				<div key={group.category} role="group" aria-label={group.category}>
					<CategoryLabel>
						<Detail color="medium">{group.category}</Detail>
					</CategoryLabel>
					{group.agents.map(agent => {
						// Two distinct, currently mutually-exclusive reasons a row can be marked: a
						// real account default (not wired up yet — always false today) versus this
						// picker's own stand-in, "the agent a bare Action click would use". Kept as
						// separate booleans rather than one, so wiring up the real default later is a
						// matter of this flag starting to vary, not a redefinition of "highlighted".
						const isAccountDefault = agent.isDefault;
						const isImplicitChoice = !isAccountDefault && agent.id === firstAgentId;
						return (
							<AgentRow
								key={agent.id}
								type="button"
								role="option"
								aria-selected={false}
								$highlighted={isAccountDefault || isImplicitChoice}
								data-cy="nova-agent-picker-row"
								onClick={() => {
									props.onSelectAgent(agent.id);
									setOpen(false);
									setQuery('');
								}}
							>
								<FlexColumn>
									<Text>{agent.title}</Text>
									{/* A missing description renders the row without a supporting
									    line rather than an empty element. */}
									{agent.description && <Detail color="medium">{agent.description}</Detail>}
								</FlexColumn>
								{(isAccountDefault || isImplicitChoice) && (
									<span
										aria-label={formatMessage({
											id: isAccountDefault
												? 'nova_invocation.picker.default_agent_marker'
												: 'nova_invocation.picker.implicit_choice_marker',
										})}
										data-cy={
											isAccountDefault
												? 'nova-agent-picker-default-mark'
												: 'nova-agent-picker-implicit-mark'
										}
									>
										✓
									</span>
								)}
							</AgentRow>
						);
					})}
				</div>
			))}
		</div>
	);

	return createPortal(content, document.body);
};
